<template>
  <div class="odontogram-form">
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p class="loading-text">Carregando odontograma...</p>
      </div>
    </div>

    <div class="form-group general-notes-section">
      <label class="form-label text-bold">Anotações gerais</label>
      <st-editor
        class="content-editor"
        id="general-notes"
        v-model="generalNotes"
        :disabled="disabled"
        @input="onGeneralNotesChange"
      />
    </div>

    <div class="divider" />

    <div class="odontograma-container">
      <div class="main-odontograma">
        <div class="chart-toolbar">
          <div class="condition-tools">
            <button
              type="button"
              v-for="condition in conditions"
              :key="condition.value"
              class="tool-btn tooltip tooltip-bottom"
              :class="{ active: appliedCondition === condition.value }"
              :disabled="!hasSelection"
              :data-tooltip="`${condition.label} - ${condition.description}`"
              @click="applyCondition(condition.value)"
            >
              <i class="tool-dot" :class="`condition-${condition.value}`" />
              <img :src="conditionImages[condition.value]" alt="" class="tool-icon" />
              <span class="tool-label">{{ condition.label }}</span>
            </button>
            <button
              type="button"
              class="tool-btn tool-btn-clear tooltip tooltip-bottom"
              :disabled="!hasSelection"
              data-tooltip="Limpar condição e faces dos dentes selecionados"
              @click="clearSelection"
            >
              <i class="icon icon-cross" />
              <span class="tool-label">Limpar</span>
            </button>
          </div>

          <label class="form-checkbox deciduous-toggle">
            <input type="checkbox" v-model="showDeciduous" />
            <i class="form-icon" /> Dentição decídua
          </label>
        </div>

        <p class="chart-hint">
          Clique no dente para selecioná-lo e nas faces para marcá-las.
          Segure <kbd>Ctrl</kbd> (ou <kbd>⌘</kbd>) para selecionar vários dentes.
        </p>

        <div class="chart-scroll">
          <div class="dental-chart">
            <div
              v-for="row in visibleRows"
              :key="row.key"
              class="arch-row"
              :class="[row.key, `arch-${row.arch}`, { 'is-lower-start': isFirstLowerRow(row) }]"
            >
              <div class="hemi hemi-right">
                <tooth-item
                  v-for="number in row.right"
                  :key="number"
                  :tooth="teethByNumber[number]"
                  :is-selected="isToothSelected(number)"
                  @tooth-clicked="selectTooth"
                  @surface-clicked="toggleSurface"
                />
              </div>
              <div class="hemi hemi-left">
                <tooth-item
                  v-for="number in row.left"
                  :key="number"
                  :tooth="teethByNumber[number]"
                  :is-selected="isToothSelected(number)"
                  @tooth-clicked="selectTooth"
                  @surface-clicked="toggleSurface"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="details-sidebar">
        <div class="tooth-details" v-if="hasSelection">
          <div class="details-header">
            <h4 class="details-title">{{ detailsTitle }}</h4>
            <button type="button" class="btn btn-link btn-sm" @click="cancelSelection">
              Fechar
            </button>
          </div>

          <div class="details-form">
            <div class="form-group">
              <label class="form-label" for="tooth-condition">
                {{ isMultiple ? 'Aplicar condição a todos os dentes' : 'Condição' }}
              </label>
              <select
                id="tooth-condition"
                class="form-select"
                :value="isMultiple ? '' : selectedTeeth[0].condition"
                @change="applyCondition($event.target.value)"
              >
                <option value="">Selecione uma condição...</option>
                <option
                  v-for="condition in conditions"
                  :key="condition.value"
                  :value="condition.value"
                >
                  {{ condition.label }}
                </option>
              </select>
            </div>

            <div class="form-group" v-if="!isMultiple">
              <label class="form-label">Faces do dente</label>
              <div class="surface-options">
                <label
                  class="form-checkbox"
                  v-for="surface in selectedToothSurfaces"
                  :key="surface.value"
                >
                  <input
                    type="checkbox"
                    :checked="isSurfaceActive(selectedTeeth[0], surface.value)"
                    @change="toggleSurface(selectedTeeth[0], surface.value)"
                  />
                  <i class="form-icon" /> {{ surface.label }}
                </label>
              </div>
            </div>

            <div class="form-group" v-if="!isMultiple">
              <label class="form-label" for="tooth-notes">Observações</label>
              <textarea
                id="tooth-notes"
                class="form-input"
                v-model="selectedTeeth[0].notes"
                @blur="saveToothData(selectedTeeth[0])"
                rows="3"
                placeholder="Digite observações sobre o dente..."
              ></textarea>
            </div>

            <div class="form-actions">
              <button type="button" class="btn btn-neutral btn-sm" @click="clearSelection">
                Limpar {{ isMultiple ? 'dentes' : 'dente' }}
              </button>
            </div>
          </div>
        </div>

        <div class="details-empty" v-else>
          <p class="empty-title">Nenhum dente selecionado</p>
          <p class="empty-subtitle">
            Selecione um dente no odontograma para registrar condição, faces e observações.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import toothHealthy from '@/assets/images/tooth/tooth-healthy.svg';
import toothCarious from '@/assets/images/tooth/tooth-carious.svg';
import toothRestored from '@/assets/images/tooth/tooth-restored.svg';
import toothCrown from '@/assets/images/tooth/tooth-crown.svg';
import toothRootCanal from '@/assets/images/tooth/tooth-root-canal.svg';
import toothMissing from '@/assets/images/tooth/tooth-missing.svg';
import ToothItem from './ToothItem.vue';
import {
  DECIDUOUS_NUMBERS,
  ODONTOGRAM_ROWS,
  TOOTH_CONDITIONS,
  getOrderedSurfaces,
  getSurfaceLabel,
  transformApiDataToOdontograma,
} from '../helpers/odontogramaHelpers';

export default {
  name: 'OdontogramForm',
  components: {
    ToothItem,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      conditions: TOOTH_CONDITIONS,
      conditionImages: {
        healthy: toothHealthy,
        carious: toothCarious,
        restored: toothRestored,
        crown: toothCrown,
        canal: toothRootCanal,
        missing: toothMissing,
      },
      selectedNumbers: [],
      allTeeth: [],
      showDeciduous: false,
      generalNotes: '',
      loading: false,
      syncedData: '',
    };
  },
  mounted() {
    this.loadGeneralNotes();
  },
  computed: {
    computedData() {
      return transformApiDataToOdontograma(this.data);
    },
    teethByNumber() {
      return this.allTeeth.reduce((acc, tooth) => {
        acc[tooth.number] = tooth;
        return acc;
      }, {});
    },
    visibleRows() {
      return ODONTOGRAM_ROWS.filter(row => this.showDeciduous || row.type === 'permanent');
    },
    selectedTeeth() {
      return this.selectedNumbers.map(number => this.teethByNumber[number]).filter(Boolean);
    },
    hasSelection() {
      return this.selectedTeeth.length > 0;
    },
    isMultiple() {
      return this.selectedTeeth.length > 1;
    },
    detailsTitle() {
      return this.isMultiple
        ? `${this.selectedTeeth.length} dentes selecionados`
        : `Dente ${this.selectedTeeth[0].number}`;
    },
    appliedCondition() {
      const [first, ...others] = this.selectedTeeth;
      if (!first || !first.condition) return '';
      return others.every(tooth => tooth.condition === first.condition) ? first.condition : '';
    },
    selectedToothSurfaces() {
      if (this.isMultiple || !this.hasSelection) return [];
      const { number } = this.selectedTeeth[0];
      return getOrderedSurfaces(number).map(surface => ({
        value: surface,
        label: getSurfaceLabel(number, surface),
      }));
    },
  },
  watch: {
    computedData: {
      immediate: true,
      handler(newData) {
        const incoming = JSON.stringify(newData);
        if (incoming === this.syncedData) return;

        this.syncedData = incoming;
        this.allTeeth = JSON.parse(incoming);
        this.selectedNumbers = this.selectedNumbers.filter(number => this.teethByNumber[number]);

        if (!this.showDeciduous) {
          this.showDeciduous = this.allTeeth.some(
            tooth => DECIDUOUS_NUMBERS.includes(tooth.number) && this.hasData(tooth),
          );
        }
      },
    },
    data: {
      immediate: true,
      deep: true,
      handler() {
        this.loadGeneralNotes();
      },
    },
  },
  methods: {
    hasData(tooth) {
      return !!(tooth.condition || tooth.notes || (tooth.surfaces && tooth.surfaces.length));
    },
    isFirstLowerRow(row) {
      return row.key === (this.showDeciduous ? 'lower-deciduous' : 'lower-permanent');
    },
    isToothSelected(number) {
      return this.selectedNumbers.includes(number);
    },
    isSurfaceActive(tooth, surface) {
      return !!tooth.surfaces && tooth.surfaces.includes(surface);
    },
    selectTooth(tooth, event) {
      if (this.disabled) {
        return;
      }

      const multiple = event && (event.ctrlKey || event.metaKey || event.shiftKey);

      if (!multiple) {
        this.selectedNumbers = this.isToothSelected(tooth.number)
        && this.selectedNumbers.length === 1
          ? []
          : [tooth.number];
        return;
      }

      const index = this.selectedNumbers.indexOf(tooth.number);
      if (index === -1) {
        this.selectedNumbers.push(tooth.number);
      } else {
        this.selectedNumbers.splice(index, 1);
      }
    },
    applyCondition(condition) {
      if (this.disabled) {
        return;
      }
      this.selectedTeeth.forEach((tooth) => {
        this.$set(tooth, 'condition', condition);
        this.saveToothData(tooth);
      });
    },
    toggleSurface(tooth, surface) {
      if (this.disabled) {
        return;
      }

      if (!tooth.surfaces) {
        this.$set(tooth, 'surfaces', []);
      }

      const index = tooth.surfaces.indexOf(surface);

      if (index === -1) {
        tooth.surfaces.push(surface);
      } else {
        tooth.surfaces.splice(index, 1);
      }

      if (!this.isToothSelected(tooth.number)) {
        this.selectedNumbers = [tooth.number];
      }

      this.saveToothData(tooth);
    },
    clearSelection() {
      if (this.disabled) {
        return;
      }
      this.selectedTeeth.forEach((tooth) => {
        this.$set(tooth, 'condition', '');
        this.$set(tooth, 'surfaces', []);
        this.$set(tooth, 'notes', '');
        this.saveToothData(tooth);
      });
    },
    cancelSelection() {
      this.selectedNumbers = [];
    },
    saveToothData(tooth) {
      if (!tooth) return;

      const surfaces = tooth.surfaces && tooth.surfaces.length > 0 ? tooth.surfaces.join(', ') : '';

      this.$emit('change', { name: `tooth${tooth.number}Surfaces`, value: surfaces });
      this.$emit('change', { name: `tooth${tooth.number}Condition`, value: tooth.condition || '' });
      this.$emit('change', { name: `tooth${tooth.number}Notes`, value: tooth.notes || '' });
    },
    onGeneralNotesChange(value) {
      this.$emit('change', { name: 'content', value });
    },
    loadGeneralNotes() {
      if (this.data && this.data.inputs) {
        let contentValue = '';

        if (Array.isArray(this.data.inputs)) {
          const contentInput = this.data.inputs.find(input => input.name === 'content');
          if (contentInput) {
            contentValue = contentInput.value || '';
          }
        } else if (typeof this.data.inputs === 'object') {
          contentValue = this.data.inputs.content || '';
        }

        this.generalNotes = contentValue;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/_odontogram";

.odontogram-form {
  position: relative;
  color: $body-font-color;

  .general-notes-section {
    margin-bottom: $layout-spacing;

    .form-label {
      margin-bottom: $layout-spacing-sm;
    }
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, .95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    border-radius: $border-radius;

    .loading-spinner {
      text-align: center;

      .spinner {
        width: 50px;
        height: 50px;
        margin: 0 auto 16px;
        border: 4px solid $border-color-dark;
        border-top: 4px solid $primary-color;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      .loading-text {
        font-weight: 600;
        color: $dark-color;
        margin: 0;
      }
    }
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .odontograma-container {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 240px;
    gap: $layout-spacing-lg;
    align-items: start;

    @media (max-width: $size-lg) {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .main-odontograma {
    background: $bg-color-light;
    border-radius: $border-radius;
    border: $border-width solid $border-color-dark;
    padding: $layout-spacing-lg;
  }

  .chart-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: $layout-spacing;
    padding-bottom: $layout-spacing;
    border-bottom: $border-width solid $border-color;

    .condition-tools {
      display: flex;
      flex-wrap: wrap;
      gap: $layout-spacing-sm;
    }

    .tool-btn {
      display: flex;
      align-items: center;
      gap: $layout-spacing-sm;
      height: $control-size;
      padding: 0 $control-padding-x;
      border: $border-width solid $border-color-dark;
      border-radius: $border-radius;
      background: $bg-color-light;
      color: $body-font-color;
      font-size: $font-size-sm;
      cursor: pointer;
      transition: all .2s ease;

      &:hover:not(:disabled) {
        border-color: $primary-color;
        background: lighten($primary-color, 45%);
      }

      &.active {
        border-color: $primary-color;
        background: lighten($primary-color, 42%);
        color: $primary-color-dark;
        font-weight: 600;
      }

      &:disabled {
        opacity: .5;
        cursor: not-allowed;
      }

      .tool-icon {
        width: 16px;
        height: 16px;
      }

      .tool-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: $bg-color;

        @each $condition, $color in $tooth-condition-colors {
          &.condition-#{$condition} {
            background: $color;
          }
        }
      }
    }

    .deciduous-toggle {
      font-size: $font-size-sm;
      margin: 0;
    }
  }

  .chart-hint {
    margin: $layout-spacing 0 0;
    font-size: $font-size-sm;
    color: $gray-color-dark;

    kbd {
      font-size: $font-size-xs;
    }
  }

  .chart-scroll {
    overflow-x: auto;
    padding: $layout-spacing-sm 0;
  }

  .dental-chart {
    position: relative;
    min-width: 700px;
    padding: $layout-spacing 0;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      left: 50%;
      border-left: $border-width dashed $border-color-dark;
    }

    .arch-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      align-items: end;

      &.is-lower-start {
        margin-top: $layout-spacing-lg;
        padding-top: $layout-spacing-lg;
        border-top: $border-width dashed $border-color-dark;
      }

      &.arch-upper {
        align-items: flex-end;
      }

      &.arch-lower {
        align-items: flex-start;
      }
    }

    .hemi {
      display: flex;
      gap: 2px;
    }

    .hemi-right {
      justify-content: flex-end;
      padding-right: $layout-spacing;
    }

    .hemi-left {
      justify-content: flex-start;
      padding-left: $layout-spacing;
    }
  }

  .details-sidebar {
    position: sticky;
    top: $layout-spacing;
    background: $bg-color-light;
    border-radius: $border-radius;
    border: $border-width solid $border-color-dark;
    padding: $layout-spacing-lg;

    .details-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: $layout-spacing;
      padding-bottom: $layout-spacing;
      margin-bottom: $layout-spacing;
      border-bottom: $border-width solid $border-color;

      .details-title {
        font-size: $font-size-lg;
        font-weight: 600;
        margin: 0;
        color: $dark-color;
      }
    }

    .details-form {
      .form-group {
        margin-bottom: $layout-spacing-lg;

        &:last-child {
          margin-bottom: 0;
        }

        .form-label {
          font-weight: 600;
        }
      }

      .surface-options {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0 $layout-spacing;

        .form-checkbox {
          font-size: $font-size-sm;
          margin: 0;
        }
      }

      .form-input {
        resize: vertical;
      }

      .form-actions {
        margin-top: $layout-spacing-lg;
      }
    }

    .details-empty {
      color: $gray-color-dark;

      .empty-title {
        font-weight: 600;
        margin: 0 0 $layout-spacing-sm;
      }

      .empty-subtitle {
        font-size: $font-size-sm;
        margin: 0;
      }
    }
  }
}
</style>

<style lang="scss">
.odontogram-form {
  .content-editor {
    .ck-editor__editable_inline {
      min-height: 5.5rem !important;
    }
  }
}
</style>
